/**
 * Created by piotr@revisual.io on 25/07/2025.
 */
import { useMemo, Fragment } from "react";
import classNames from "classnames";
import { QUOTAS } from "../../../../../consts";
import Badge from "../../../theme/untitled/Badge/Badge";
import {
  QUOTA_LABELS,
  getQuotaLevel,
  formatNumber,
} from "./utils";

const ShowBarFor = [
  QUOTAS.widgets,
  QUOTAS.integrations,
  QUOTAS.domains,
  QUOTAS.team,
  QUOTAS.views,
];

// Untracked (footer-only) quotas: the figure text and the "why" note behind
// it, shared by both the vertical <dl> rows and the horizontal collapsed
// sentence — only the surrounding punctuation/casing differs per mode.
const FOOTER_NOTE = {
  [QUOTAS.events]: "not enforced yet",
  [QUOTAS.eventsMonthsAhead]: "hard limit",
};

function getFooterFigureText(quotaKey, quota) {
  if (quota === -1) return "Unlimited";
  if (quota === 0) return "Not included";
  if (quotaKey === QUOTAS.eventsMonthsAhead) {
    return quota === 1 ? "1 month" : `${quota} months`;
  }
  return formatNumber(quota);
}

// Kept unchanged and exported as-is — reused by
// src/apps/admin/pages/Reports/PlanUsage/UsageCard.js. Not part of this
// handoff's scope (assets/handoffs/design_handoff_quota_summary applies to
// the SubscriptionLimits card only).
export const QuotaItemProgressBar = ({ quotaKey, quota, usage }) => {
  const percentage = usage ? (usage / quota) * 100 : 0;
  const isExceeded = usage >= quota;

  const progressBarStyle = useMemo(
    () => ({
      width: `${percentage}%`,
    }),
    [percentage]
  );

  return quota > 0 && ShowBarFor.includes(quotaKey) ? (
    <div className={classNames("quota-progress", { "bg-danger": isExceeded })}>
      <div
        className={classNames("progress-bar", {
          "bg-danger": isExceeded,
          "bg-success": !isExceeded,
        })}
        style={progressBarStyle}
        role="progressbar"
        aria-valuenow={usage}
        aria-valuemin="0"
        aria-valuemax={quota}
      ></div>
    </div>
  ) : null;
};

function QuotaBadge({ usage, quota }) {
  if (!(quota > 0)) return null;

  const level = getQuotaLevel(usage, quota);

  if (level === "room") return null;

  if (level === "full") {
    return (
      <Badge color="warning" className="qs-badge-filled">
        Limit reached
      </Badge>
    );
  }

  const left = Math.max(0, quota - (usage || 0));
  return <Badge color="warning">{formatNumber(left)} left</Badge>;
}

/**
 * @param {Object} props
 * @param {number} props.usage
 * @param {number} props.quota
 * @param {"sm"|"lg"=} props.size - "lg" bumps the strong figure to
 *   text-lg (horizontal/strip mode); omitted keeps today's vertical sizing.
 */
function QuotaValue({ usage, quota, size }) {
  const sizeClass = size === "lg" ? "qs-val--lg" : null;

  if (quota === -1) {
    return <span className={classNames("qs-val", sizeClass)}>Unlimited</span>;
  }

  if (quota === 0) {
    return (
      <span className={classNames("qs-val", sizeClass, "qs-val--muted")}>
        Not included
      </span>
    );
  }

  const used = usage || 0;
  const isZero = used === 0;

  return (
    <span
      className={classNames("qs-val", sizeClass, {
        "qs-val--zero": isZero,
      })}
    >
      <strong>{isZero ? "—" : formatNumber(used)}</strong> of{" "}
      {formatNumber(quota)}
    </span>
  );
}

export function QuotaTrack({ usage, quota }) {
  if (!(quota > 0)) return null;

  const used = usage || 0;
  if (used === 0) return <span className="qs-track" />;

  const pct = Math.min(100, Math.max(6, Math.round((used / quota) * 100)));
  const level = getQuotaLevel(used, quota);

  return (
    <span className="qs-track">
      <i
        className={classNames("qs-fill", {
          "qs-fill--warn": level === "near",
          "qs-fill--full": level === "full",
        })}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
}

/**
 * A single tracked quota row — label, optional "this month" qualifier,
 * escalation badge, `x of y` value, and the usage bar.
 *
 * @param {Object}  props
 * @param {string}  props.quotaKey
 * @param {number}  props.quota
 * @param {number}  props.usage
 * @param {boolean} props.resetsMonthly
 */
const QuotaItem = ({ quotaKey, quota, usage, resetsMonthly }) => {
  return (
    <div className="qs-row">
      <span className="qs-label">
        {QUOTA_LABELS[quotaKey]}
        {resetsMonthly && <span className="qs-per">this month</span>}
        <QuotaBadge usage={usage} quota={quota} />
      </span>
      <QuotaValue usage={usage} quota={quota} />
      <QuotaTrack usage={usage} quota={quota} />
    </div>
  );
};

/**
 * An untracked quota (no `x of y`, no bar) — shown as a `Limit {y}`
 * definition-list row in the card footer.
 *
 * @param {Object}  props
 * @param {string}  props.quotaKey
 * @param {number}  props.quota
 * @param {boolean} props.resetsMonthly
 */
export const QuotaFooterItem = ({ quotaKey, quota, resetsMonthly }) => {
  const figure = getFooterFigureText(quotaKey, quota);
  const note = FOOTER_NOTE[quotaKey];
  const value =
    quota === -1 || quota === 0 ? (
      figure
    ) : (
      <>
        Limit <strong>{figure}</strong>
        {note ? ` — ${note}` : ""}
      </>
    );

  return (
    <>
      <dt>
        {QUOTA_LABELS[quotaKey]}
        {resetsMonthly && <span className="qs-per">this month</span>}
      </dt>
      <dd>{value}</dd>
    </>
  );
};

/**
 * Horizontal ("strip") mode's per-quota item — a vertical stack instead of
 * the vertical mode's label/value row: label (with badge/qualifier) on top,
 * a larger value, then the bar. Same data, same thresholds — only the
 * arrangement and the value's type size differ from QuotaItem.
 *
 * @param {Object}  props
 * @param {string}  props.quotaKey
 * @param {number}  props.quota
 * @param {number}  props.usage
 * @param {boolean} props.resetsMonthly
 */
export const QuotaStripItem = ({ quotaKey, quota, usage, resetsMonthly }) => {
  return (
    <div className="qs-strip-item">
      <span className="qs-strip-label">
        {QUOTA_LABELS[quotaKey]}
        {resetsMonthly && <span className="qs-per">this month</span>}
        <QuotaBadge usage={usage} quota={quota} />
      </span>
      <QuotaValue usage={usage} quota={quota} size="lg" />
      <QuotaTrack usage={usage} quota={quota} />
    </div>
  );
};

/**
 * Horizontal mode collapses the two untracked quotas into one footer
 * sentence instead of the vertical mode's separate <dl> rows.
 *
 * @param {Object} props
 * @param {Array<{quotaKey: string, quota: number, resetsMonthly: boolean}>} props.items
 */
export const QuotaStripFooter = ({ items }) => (
  <p className="qs-strip-foot">
    {items.map(({ quotaKey, quota, resetsMonthly }) => {
      const note = FOOTER_NOTE[quotaKey];
      return (
        <Fragment key={quotaKey}>
          {QUOTA_LABELS[quotaKey]}
          {resetsMonthly ? " this month" : ""}: limit{" "}
          <strong>{getFooterFigureText(quotaKey, quota)}</strong>
          {note ? `, ${note}` : ""}
          {". "}
        </Fragment>
      );
    })}
  </p>
);

export default QuotaItem;
